import { useState, useCallback, useEffect, useRef } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { reportError } from '@/lib/errors';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
  withSequence,
  cancelAnimation,
} from 'react-native-reanimated';
import { Mic, MicOff, Square, AlertCircle } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import AnimatedPressable from '@/components/AnimatedPressable';

interface VoiceInputProps {
  onTranscript: (text: string) => void;
  onPartialTranscript?: (text: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

// Check if Web Speech API is available
const isSpeechRecognitionSupported = () => {
  if (Platform.OS !== 'web') return false;
  return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
};

type RecordingState = 'idle' | 'recording' | 'processing' | 'error';

export function VoiceInput({
  onTranscript,
  onPartialTranscript,
  placeholder = 'Tap to speak',
  disabled = false,
}: VoiceInputProps) {
  const colors = useThemeColors();
  const [state, setState] = useState<RecordingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [partialText, setPartialText] = useState('');
  const recognitionRef = useRef<any>(null);

  // Animation for recording indicator
  const pulseScale = useSharedValue(1);
  const pulseOpacity = useSharedValue(0.5);

  const pulseStyle = useAnimatedStyle(() => ({
    transform: [{ scale: pulseScale.value }],
    opacity: pulseOpacity.value,
  }));

  const startPulseAnimation = useCallback(() => {
    pulseScale.value = withRepeat(
      withSequence(
        withTiming(1.3, { duration: 500 }),
        withTiming(1, { duration: 500 })
      ),
      -1,
      false
    );
    pulseOpacity.value = withRepeat(
      withSequence(
        withTiming(0.8, { duration: 500 }),
        withTiming(0.3, { duration: 500 })
      ),
      -1,
      false
    );
  }, [pulseScale, pulseOpacity]);

  const stopPulseAnimation = useCallback(() => {
    cancelAnimation(pulseScale);
    cancelAnimation(pulseOpacity);
    pulseScale.value = withTiming(1, { duration: 200 });
    pulseOpacity.value = withTiming(0.5, { duration: 200 });
  }, [pulseScale, pulseOpacity]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  const startRecording = useCallback(() => {
    if (Platform.OS !== 'web' || !isSpeechRecognitionSupported()) {
      setError('Voice input is not supported on this device');
      setState('error');
      return;
    }

    setError(null);
    setPartialText('');

    try {
      // @ts-ignore - Web Speech API types
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setState('recording');
        startPulseAnimation();
      };

      recognition.onresult = (event: any) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        if (interimTranscript) {
          setPartialText(interimTranscript);
          onPartialTranscript?.(interimTranscript);
        }

        if (finalTranscript) {
          onTranscript(finalTranscript);
          setPartialText('');
        }
      };

      recognition.onerror = (event: any) => {
        reportError(event.error, { context: 'VoiceInput.recognition.onerror', level: 'warning' });
        if (event.error === 'not-allowed') {
          setError('Microphone access denied. Please allow microphone access.');
        } else if (event.error === 'no-speech') {
          setError('No speech detected. Please try again.');
        } else {
          setError(`Error: ${event.error}`);
        }
        setState('error');
        stopPulseAnimation();
      };

      recognition.onend = () => {
        setState('idle');
        stopPulseAnimation();
        setPartialText('');
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      reportError(err, { context: 'VoiceInput.startRecording' });
      setError('Failed to start voice input');
      setState('error');
    }
  }, [onTranscript, onPartialTranscript, startPulseAnimation, stopPulseAnimation]);

  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setState('processing');
    stopPulseAnimation();

    // Brief processing state then idle
    setTimeout(() => {
      setState('idle');
    }, 500);
  }, [stopPulseAnimation]);

  const handlePress = () => {
    if (disabled) return;

    if (state === 'recording') {
      stopRecording();
    } else if (state === 'idle' || state === 'error') {
      startRecording();
    }
  };

  // Web unsupported or Native platform
  if (!isSpeechRecognitionSupported()) {
    return (
      <View
        className="flex-row items-center gap-2 px-3 py-2 rounded-xl"
        style={{ backgroundColor: colors.surface.secondary }}
      >
        <MicOff size={18} color={colors.text.tertiary} />
        <Text style={{ color: colors.text.tertiary }} className="text-xs">
          Voice input available on web
        </Text>
      </View>
    );
  }

  return (
    <View className="items-center">
      {/* Recording Button */}
      <AnimatedPressable
        onPress={handlePress}
        disabled={disabled || state === 'processing'}
        className="relative"
      >
        {/* Pulse Animation Background */}
        {state === 'recording' && (
          <Animated.View
            style={[
              pulseStyle,
              {
                position: 'absolute',
                width: 56,
                height: 56,
                borderRadius: 28,
                backgroundColor: colors.traffic.red,
              },
            ]}
          />
        )}

        {/* Main Button */}
        <View
          className="w-14 h-14 rounded-full items-center justify-center"
          style={{
            backgroundColor: state === 'recording'
              ? colors.traffic.red
              : state === 'error'
              ? colors.traffic.redLight
              : disabled
              ? colors.border.light
              : colors.primary[600],
          }}
        >
          {state === 'recording' ? (
            <Square size={20} color={colors.text.inverse} />
          ) : state === 'error' ? (
            <AlertCircle size={24} color={colors.traffic.red} />
          ) : (
            <Mic
              size={24}
              color={disabled ? colors.text.tertiary : colors.text.inverse}
            />
          )}
        </View>
      </AnimatedPressable>

      {/* Status Text */}
      <View className="mt-2 min-h-5">
        {state === 'idle' && !error && (
          <Text style={{ color: colors.text.tertiary }} className="text-xs text-center">
            {placeholder}
          </Text>
        )}
        {state === 'recording' && (
          <Text style={{ color: colors.traffic.red }} className="text-xs text-center font-medium">
            Listening... tap to stop
          </Text>
        )}
        {state === 'processing' && (
          <Text style={{ color: colors.primary[600] }} className="text-xs text-center">
            Processing...
          </Text>
        )}
        {error && (
          <Text style={{ color: colors.traffic.red }} className="text-xs text-center">
            {error}
          </Text>
        )}
      </View>

      {/* Partial Transcript */}
      {partialText && (
        <View
          className="mt-2 px-3 py-2 rounded-lg max-w-[200px]"
          style={{ backgroundColor: colors.surface.secondary }}
        >
          <Text
            style={{ color: colors.text.secondary }}
            className="text-sm italic text-center"
            numberOfLines={2}
          >
            "{partialText}"
          </Text>
        </View>
      )}
    </View>
  );
}

/**
 * Inline voice input button that can be placed next to a text input
 */
interface InlineVoiceInputProps {
  onTranscript: (text: string) => void;
  disabled?: boolean;
}

export function InlineVoiceInput({ onTranscript, disabled = false }: InlineVoiceInputProps) {
  const colors = useThemeColors();
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef<any>(null);

  // Animation
  const scale = useSharedValue(1);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  useEffect(() => {
    if (isRecording) {
      scale.value = withRepeat(
        withSequence(
          withTiming(1.1, { duration: 300 }),
          withTiming(1, { duration: 300 })
        ),
        -1,
        false
      );
    } else {
      cancelAnimation(scale);
      scale.value = withTiming(1, { duration: 150 });
    }
  }, [isRecording, scale]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  const toggleRecording = useCallback(() => {
    if (!isSpeechRecognitionSupported()) return;

    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
      return;
    }

    try {
      // @ts-ignore
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => setIsRecording(true);

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        onTranscript(transcript);
      };

      recognition.onerror = () => setIsRecording(false);
      recognition.onend = () => setIsRecording(false);

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      reportError(err, { context: 'VoiceInputCompact.toggleRecording', level: 'warning' });
    }
  }, [isRecording, onTranscript]);

  if (!isSpeechRecognitionSupported()) {
    return null;
  }

  return (
    <AnimatedPressable onPress={toggleRecording} disabled={disabled}>
      <Animated.View
        style={[
          animatedStyle,
          {
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: isRecording ? colors.traffic.red : colors.primary[100],
            alignItems: 'center',
            justifyContent: 'center',
          },
        ]}
      >
        <Mic
          size={20}
          color={isRecording ? colors.text.inverse : colors.primary[600]}
        />
      </Animated.View>
    </AnimatedPressable>
  );
}
